import type { BadgeTone } from '@paige/ui';
import type {
  AccountState,
  Channel,
  EnterpriseQuote,
  Interval,
  PlanKey,
  StatementSummary,
} from './pb-types';

/**
 * The words the partner billing pages use (ADR-0036). Staff read "Ademero invoice" for the statement channel
 * (imported into QuickBooks) and "Online invoice" for Stripe's; the wire values stay on the wire.
 */
export const channelWord: Record<Channel, string> = {
  quickbooks: 'Ademero invoice',
  stripe: 'Online invoice',
};

export const channelHint: Record<Channel, string> = {
  quickbooks: 'A monthly statement, imported into QuickBooks by accounting',
  stripe: 'Stripe subscription and invoices, paid online',
};

export const stateWord: Record<AccountState, string> = {
  good: 'In good standing',
  lapsed: 'Lapsed: online invoice unpaid',
  suspended: 'Suspended by hand',
};

export const stateTone: Record<AccountState, BadgeTone> = {
  good: 'success',
  lapsed: 'danger',
  suspended: 'warning',
};

const PLAN: Record<PlanKey, string> = {
  free: 'Free',
  starter: 'Starter',
  pro: 'Pro',
  business: 'Business',
  enterprise: 'Enterprise',
};

export const planWord = (p: string | null | undefined): string =>
  p ? (PLAN[p as PlanKey] ?? p) : '—';

export const intervalWord = (i: Interval | null | undefined): string =>
  i === 'year' ? 'yearly' : i === 'month' ? 'monthly' : '';

const DAY = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  timeZone: 'UTC',
});

/** A calendar day (yyyy-MM-dd) or the day of an instant, as Paige's billing calendar (UTC) has it: "Oct 5, 2026". */
export function day(iso: string | null | undefined): string {
  if (!iso) return '—';
  const at = iso.length === 10 ? `${iso}T00:00:00Z` : iso;
  const t = Date.parse(at);
  return Number.isNaN(t) ? iso : DAY.format(t);
}

/** The day before a calendar day: a period's last day is the day before its renewal. */
export function dayBefore(iso: string | null | undefined): string | null {
  if (!iso) return null;
  return new Date(Date.parse(`${iso.slice(0, 10)}T00:00:00Z`) - 86_400_000)
    .toISOString()
    .slice(0, 10);
}

const MONTH = new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });

/** "2026-10" as "October 2026". */
export const monthWord = (m: string): string => MONTH.format(Date.parse(`${m}-01T00:00:00Z`));

/** Dollars to the cent, a credit with its minus: "$1,240.50", "−$12.50". */
export function dollars(n: number): string {
  const s = Math.abs(n).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  return n < 0 ? `−${s}` : s;
}

/** A price a page, to four places when it needs them: "$0.04", "$0.0475". */
export const perPage = (n: number): string =>
  n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  });

/** A quote's terms in a line: "25,000 pages a month at $24,000.00 a year". */
export function quoteTerms(q: EnterpriseQuote): string {
  const pages = `${q.pagesPerMonth.toLocaleString('en-US')} pages a month`;
  if (q.pricePerPeriod === null) return pages;
  return `${pages} at ${dollars(q.pricePerPeriod)} a ${q.interval}`;
}

export type StatementLook = { word: string; tone: BadgeTone };

/** How a statement stands: accruing, due, overdue, paid or void. @param today yyyy-MM-dd */
export function statementLook(s: StatementSummary, today: string): StatementLook {
  if (s.state === 'open') return { word: 'Open · accruing', tone: 'accent' };
  if (s.state === 'void') return { word: 'Void · reissued', tone: 'neutral' };
  if (s.paidOn) return { word: 'Paid', tone: 'success' };
  if (s.dueOn && s.dueOn < today) return { word: 'Overdue', tone: 'danger' };
  return { word: 'Issued · due', tone: 'warning' };
}

/** A company count: "1 company", "7 companies". */
export const companiesWord = (n: number): string =>
  `${n.toLocaleString('en-US')} ${n === 1 ? 'company' : 'companies'}`;

/** Today in Paige's billing calendar (UTC), yyyy-MM-dd. */
export const todayUtc = (now: number = Date.now()): string =>
  new Date(now).toISOString().slice(0, 10);
